import type { OmitFunctions } from '@seen/core/types'
import type {
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table'
import { createStore } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { TABLE_DEFAULT_PAGE_SIZE } from '#src/constants/tables.ts'
import { createZustandContext } from '#src/utils/zustand/Context.tsx'
import {
  createStoreQueryParamBuilder,
  createURLPersistentStorage,
} from '#src/utils/zustand/zustand.ts'

export type ReactTableUrlState = {
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
  showColumnFilters: boolean
  setShowColumnFilters: OnChangeFn<boolean>
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
  globalFilter: string
  setGlobalFilter: (filter: string) => void
  pageIndex: number
  setPageIndex: (index: number) => void
  pageSize: number
  setPageSize: (size: number) => void
}

export type ReactTableUrlStoreOptions = {
  name: string
  defaultSort?: SortingState
  defaultPageSize?: number
}

export const createReactTableUrlStore = ({
  name,
  defaultSort = [],
  defaultPageSize = TABLE_DEFAULT_PAGE_SIZE,
}: ReactTableUrlStoreOptions): ReturnType<
  ReturnType<typeof createStore<ReactTableUrlState>>
> => {
  const storageOptions = {
    name,
    storage: createJSONStorage<ReactTableUrlState>(() =>
      createURLPersistentStorage<ReactTableUrlState>()
    ),
  }

  const defaultState = {
    columnFilters: [],
    showColumnFilters: false,
    sorting: defaultSort,
    globalFilter: '',
    pageIndex: 0,
    pageSize: defaultPageSize,
  } satisfies Partial<OmitFunctions<ReactTableUrlState>>

  return createStore(
    persist<ReactTableUrlState>(
      (set) => ({
        ...defaultState,
        setColumnFilters: (updaterOrValue) => {
          set((state) => ({
            columnFilters:
              typeof updaterOrValue === 'function'
                ? updaterOrValue(state.columnFilters)
                : updaterOrValue,
          }))
        },
        setShowColumnFilters: (updaterOrValue) => {
          set((state) => ({
            showColumnFilters:
              typeof updaterOrValue === 'function'
                ? updaterOrValue(state.showColumnFilters)
                : updaterOrValue,
          }))
        },
        setSorting: (updaterOrValue) => {
          set((state) => ({
            sorting:
              typeof updaterOrValue === 'function'
                ? updaterOrValue(state.sorting)
                : updaterOrValue,
          }))
        },
        setGlobalFilter: (filter) => set({ globalFilter: filter }),
        setPageIndex: (index) => set({ pageIndex: index }),
        setPageSize: (size) => set({ pageSize: size }),
      }),
      storageOptions
    )
  )
}

export const createReactTableUrlContext = (
  options: ReactTableUrlStoreOptions
): ReturnType<typeof createZustandContext<ReactTableUrlState>> =>
  createZustandContext(() => createReactTableUrlStore(options))

export const buildReactTableUrlQueryParam = (
  name: string
): ReturnType<typeof createStoreQueryParamBuilder<ReactTableUrlState>> =>
  createStoreQueryParamBuilder<ReactTableUrlState>(name)
